import { AppState } from "@Store/index";
import { useEffect } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { shouldFetchProperty, fetchPropertyPageDataIfNeeded } from "./data/propertyPageReducer";
import { updateInventoryId } from "@App/appReducer";
import { usePageInteractiveSignal } from "./usePageInteractiveSignal";
import { usePropertyRepaintNudge } from "./usePropertyRepaintNudge";

const usePropertyPage = (props: any) => {
  const { location } = props;

  const dispatch = useDispatch();

  const matchId = props?.match?.params?.id;

  const {
    isError,
    isLoading,
    isStatus500,
    showFullPageLoader,
    error,
    id,
    isInternal,
    hasDataForCanonical,
  } = useSelector(({ propertyPageData, appData }: AppState) => {
    // Stringified once, not twice. Not named `error` — that would shadow the destructured one.
    const serializedError = JSON.stringify(propertyPageData.error);

    return {
      isError: propertyPageData.readyStatus === "failure",
      // `Boolean(...)` guards the no-error case, which used to report `true`.
      isStatus500: Boolean(serializedError) && !serializedError.includes("404"),
      error: serializedError,
      isLoading:
        propertyPageData.readyStatus === "request" ||
        shouldFetchProperty(propertyPageData, matchId, "", true) ||
        appData?.isLoadingTranslations,
      showFullPageLoader:
        !propertyPageData.data ||
        propertyPageData.data.canonical_name !== matchId ||
        appData?.isLoadingTranslations,
      id: propertyPageData?.data?.id,
      isInternal: appData?.isInternalUser,
      hasDataForCanonical: propertyPageData?.data?.canonical_name === matchId,
    };
  }, shallowEqual);

  const canonicalName = hasDataForCanonical ? matchId : undefined;
  usePageInteractiveSignal(canonicalName);
  usePropertyRepaintNudge(canonicalName);

  useEffect(() => {
    if (isError && isStatus500) {
      if (typeof window !== undefined) {
        window?.Sentry?.captureException(error);
      }
    }
  }, [isError, isStatus500]);

  useEffect(() => {
    // @ts-ignore
    if (typeof fbq !== "undefined" && fbq !== null) {
      const contentIds = id ? [id?.toString()] : undefined;

      // @ts-ignore
      fbq(
        "track",
        "ViewContent",
        contentIds ? { content_ids: contentIds, content_type: "product" } : {},
      );
    }
    dispatch(updateInventoryId({ inventoryId: `${id}` }));
  }, [id]);

  useEffect(() => {
    dispatch(fetchPropertyPageDataIfNeeded(props));
  }, [dispatch, location.pathname]);

  return {
    isLoading,
    isError,
    isStatus500,
    showFullPageLoader,
    isInternal,
    hasDataForCanonical,
  };
};

export default usePropertyPage;
